// Picks a legible overlay text colour (near-white or dark charcoal) for a
// given hex background, by relative luminance. Shared by any chart
// controller that draws a value/percentage label directly on top of a
// coloured segment or bar, where the background colour can vary per element.
export function readableTextColor(hex) {
  const clean = (hex || '#003d68').replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#1f2937' : '#ffffff'
}

// Canvas datalabel font size/weight for a chart_config label_size/label_weight
// value (card #3613 round 12). "medium"/"bold" match today's hardcoded values
// in bar_chart_controller.js and chart_preview_controller.js's datalabels, so
// an unset label_size/label_weight renders identically to before this round.
const LABEL_FONT_SIZES = { small: 10, medium: 12, large: 15 }
export function labelFontSize(size) { return LABEL_FONT_SIZES[size] ?? LABEL_FONT_SIZES.medium }

const LABEL_FONT_WEIGHTS = { normal: 'normal', semibold: '600', bold: 'bold' }
export function labelFontWeight(weight) { return LABEL_FONT_WEIGHTS[weight] ?? LABEL_FONT_WEIGHTS.bold }

// Resolves the percentage a breakdown segment's datalabel should show when
// label_style is "both" or "percentage_of_responses" (card #3613 round 17).
// Both styles pair a segment's raw count with the same resolved percentage --
// the segment's share of the resolved Measure Against denominator when one is
// available, falling back to the segment's share of the chart's own total
// (chartOwnTotal, e.g. all response_distribution segments summed) when no
// denominator is set. "percentage" style deliberately does NOT use this --
// it always means "share of the chart's own total", never the denominator.
export function resolvedSegmentPercentage(value, chartOwnTotal, denominator) {
  if (denominator > 0) return Math.round(value / denominator * 100)
  return chartOwnTotal > 0 ? Math.round(value / chartOwnTotal * 100) : 0
}

// Wraps a label onto up to `maxLines` lines, breaking on word boundaries;
// anything still too long after that gets ellipsis-truncated on the final
// line, so the clip always happens at an explicit "…" rather than mid-word
// at an arbitrary pixel. Originally lived only in bar_chart_controller.js
// (card #4273); promoted here so chart_preview_controller.js's and
// distribution_chart_controller.js's own horizontal-bar category axes can
// share the same wrap behaviour instead of carrying a second copy (card
// #4273 round 2).
export function wrapLabel(label, maxCharsPerLine = 18, maxLines = 2) {
  const words = String(label).split(' ')
  const lines = []
  let current = ''

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (candidate.length > maxCharsPerLine && current) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)

  if (lines.length > maxLines) {
    const truncated = lines.slice(0, maxLines)
    const lastLine = truncated[maxLines - 1]
    truncated[maxLines - 1] = lastLine.length >= maxCharsPerLine
      ? `${lastLine.slice(0, maxCharsPerLine - 1)}…`
      : `${lastLine}…`
    return truncated
  }

  return lines
}
